import type {
    AddressCandidate,
    AddressCandidatesResponse,
} from "../types/addressCandidate";
import { mlsSaleType } from "./mlsListingType";

// Below the api's $100 monthly-rent floor a lease figure is a per-sq-ft rate
// (1741038: 16.5), so it is shown bare, without a period.
const MIN_MONTHLY_RENT = 100;

const money = (amount: number): string =>
    `$${amount.toLocaleString("en-US", {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
    })}`;

export const candidatePriceLabel = (candidate: AddressCandidate): string => {
    if (mlsSaleType(candidate.propertyType) === "rent") {
        const amount = candidate.leaseAmount ?? candidate.price;
        if (amount == null) return "Price not listed";
        if (amount < MIN_MONTHLY_RENT) return money(amount);
        const yearly = /year|annual/i.test(candidate.leaseAmountFrequency ?? "");
        return `${money(amount)}/${yearly ? "yr" : "mo"}`;
    }
    if (candidate.price == null) return "Price not listed";
    return money(candidate.price);
};

const STATUS_LABEL: Record<string, string> = {
    Active: "Active",
    ComingSoon: "Coming Soon",
    ActiveUnderContract: "Under Contract",
};

export const candidateFactsLine = (candidate: AddressCandidate): string => {
    const parts: string[] = [];
    if (candidate.unitNumber) parts.push(`Unit ${candidate.unitNumber}`);
    if (candidate.status) {
        parts.push(STATUS_LABEL[candidate.status] ?? candidate.status);
    }
    if (candidate.photosCount != null && candidate.photosCount > 0) {
        parts.push(
            `${candidate.photosCount} ${candidate.photosCount === 1 ? "photo" : "photos"}`,
        );
    }
    if (candidate.lotSizeAcres != null && candidate.lotSizeAcres > 0) {
        parts.push(`${candidate.lotSizeAcres.toFixed(2)} acres`);
    }
    if (candidate.buildingAreaTotal != null && candidate.buildingAreaTotal > 0) {
        parts.push(
            `${Math.round(candidate.buildingAreaTotal).toLocaleString("en-US")} sq ft`,
        );
    }
    return parts.join(" · ");
};

export const candidateAgentLine = (candidate: AddressCandidate): string => {
    const who = [candidate.listAgentName, candidate.listOfficeName]
        .filter(Boolean)
        .join(", ");
    return who ? `Listed by ${who}` : "";
};

export const candidateHeading = (count: number, address: string): string =>
    `We found ${count} MLS listings at ${address}. They are listed separately, so choose the one you want to add.`;

// POST /listing answers with either the listing preview or this shape.
export const isAddressCandidatesResponse = (
    data: unknown,
): data is AddressCandidatesResponse =>
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { candidates?: unknown }).candidates);
